// Import a grading / competency worksheet (CSV) → a framework.
//
// Two modes:
//  • Auto (Dana AI)  — Dana reads any layout (grade-per-row, point-factor,
//    transposed, English/Swahili) and proposes a full Definition. Robust to
//    unknown structures. (V2)
//  • Manual mapping  — deterministic grade-per-row mapper: you pick which columns
//    are Grade / Titles / Competencies / factors. Predictable, no AI. (V1)
//
// Either way, nothing is created until you click Create, which POSTs the assembled
// Definition to the engine (which re-validates it).
import React, { useMemo, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { Loader2, UploadCloud, FileSpreadsheet, Sparkles, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { createFramework, aiFrameworkFromWorksheet, type AiFrameworkResult } from '@/services/workforceService';
import {
  parseCsv, parseXlsx, guessMapping, buildDefinition, type ParsedCsv, type ColumnMapping,
} from './lib/importFramework';

const inputCls =
  'w-full h-9 px-3 rounded-md border border-border bg-card text-sm disabled:bg-muted disabled:text-muted-foreground';

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

type Mode = 'ai' | 'manual';

const countDef = (def: any) => ({
  grades: def?.grades?.length ?? 0,
  factors: def?.factors?.length ?? 0,
  competencies: def?.competencies?.length ?? 0,
});

export const ImportFrameworkDialog: React.FC<{ onClose: () => void; onDone: () => void }> = ({
  onClose,
  onDone,
}) => {
  const { t } = useTranslation('workforce');
  const [mode, setMode] = useState<Mode>('ai');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [fileName, setFileName] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [industry, setIndustry] = useState('');
  const [saving, setSaving] = useState(false);
  // manual mode
  const [map, setMap] = useState<ColumnMapping | null>(null);
  // ai mode
  const [ai, setAi] = useState<AiFrameworkResult | null>(null);
  const [aiBusy, setAiBusy] = useState(false);

  const resetForFile = (p: ParsedCsv, fname: string) => {
    setParsed(p);
    setMap(guessMapping(p.headers));
    setAi(null);
    setFileName(fname);
    if (!name) setName(fname.replace(/\.[^.]+$/, ''));
  };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    try {
      const isXlsx = /\.xlsx?$/i.test(f.name);
      const p = isXlsx ? await parseXlsx(f) : parseCsv(await f.text());
      if (p.headers.length === 0 || p.rows.length === 0) {
        toast.error(t('imp_empty', { defaultValue: 'That file has no rows I can read.' }));
        return;
      }
      resetForFile(p, f.name);
    } catch (e) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('imp_read_fail', { defaultValue: 'Could not read that file.' }))));
    }
  };

  const runAi = async () => {
    if (!parsed) return;
    setAiBusy(true);
    try {
      const res = await aiFrameworkFromWorksheet({
        name: name || fileName,
        headers: parsed.headers,
        sample_rows: parsed.rows.slice(0, 20),
        total_rows: parsed.rows.length,
      });
      setAi(res);
      if (res.name) setName(res.name);
      if (res.category) setCategory(res.category);
      if (res.industry) setIndustry(res.industry);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('imp_ai_fail', { defaultValue: 'Dana could not read that worksheet — try Manual mapping.' }))));
    } finally {
      setAiBusy(false);
    }
  };

  const manualBuilt = useMemo(() => {
    if (mode !== 'manual' || !parsed || !map) return null;
    try {
      return buildDefinition(parsed.rows, map);
    } catch {
      return null;
    }
  }, [mode, parsed, map]);

  const activeDefinition = mode === 'ai' ? ai?.definition ?? null : manualBuilt?.definition ?? null;
  const activeSummary = activeDefinition ? countDef(activeDefinition) : null;

  const toggleFactor = (h: string) => {
    if (!map) return;
    setMap({
      ...map,
      factorCols: map.factorCols.includes(h)
        ? map.factorCols.filter((c) => c !== h)
        : [...map.factorCols, h],
    });
  };

  const create = async () => {
    if (!name.trim()) {
      toast.error(t('imp_need_name', { defaultValue: 'Give the framework a name.' }));
      return;
    }
    if (!activeDefinition || (activeSummary?.factors ?? 0) === 0) {
      toast.error(t('imp_need_factor', { defaultValue: 'The framework needs at least one factor.' }));
      return;
    }
    setSaving(true);
    try {
      await createFramework({
        name: name.trim(),
        description: t('imp_desc', { defaultValue: 'Imported from {{file}}', file: fileName || 'a worksheet' }),
        category: category.trim(),
        industry: industry.trim(),
        definition: activeDefinition,
      });
      toast.success(t('imp_created', { defaultValue: 'Framework created. You can now use or list it.' }));
      onDone();
      onClose();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, errMsg(e, t('imp_create_fail', { defaultValue: 'Could not create the framework.' }))));
    } finally {
      setSaving(false);
    }
  };

  const headerOptions = parsed?.headers ?? [];
  const canCreate = !!activeDefinition && (activeSummary?.factors ?? 0) > 0 && !!name.trim();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('imp_title', { defaultValue: 'Import framework' })}</DialogTitle>
        </DialogHeader>

        {/* Mode toggle */}
        <div className="flex items-center gap-1 p-1 rounded-lg bg-muted w-fit">
          <ModeBtn active={mode === 'ai'} onClick={() => setMode('ai')} icon={<Sparkles className="w-3.5 h-3.5" />}
            label={t('imp_mode_ai', { defaultValue: 'Auto (Dana AI)' })} />
          <ModeBtn active={mode === 'manual'} onClick={() => setMode('manual')} icon={<Wrench className="w-3.5 h-3.5" />}
            label={t('imp_mode_manual', { defaultValue: 'Manual mapping' })} />
        </div>

        <div className="space-y-4">
          {/* Upload */}
          <label className="flex items-center gap-3 p-4 rounded-lg border border-dashed border-border cursor-pointer hover:bg-muted/50">
            <UploadCloud className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1">
              <div className="text-sm text-foreground flex items-center gap-2">
                <span>{fileName || t('imp_upload', { defaultValue: 'Choose a grading worksheet (CSV, TSV, Excel)' })}</span>
                {parsed && (
                  <Badge variant="secondary" className="uppercase text-[10px]">
                    {parsed.delimiter === 'xlsx'
                      ? t('imp_fmt_excel', { defaultValue: 'Excel' })
                      : parsed.delimiter === '\t'
                        ? t('imp_fmt_tsv', { defaultValue: 'TSV' })
                        : t('imp_fmt_csv', { defaultValue: 'CSV' })}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {mode === 'ai'
                  ? t('imp_upload_ai', { defaultValue: 'Dana reads any layout. Nothing is uploaded until you click Create.' })
                  : t('imp_upload_hint', { defaultValue: 'One row per grade; columns become factors.' })}
              </div>
            </div>
            <input type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
          </label>

          {parsed && (
            <>
              {/* Metadata */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Field label={t('imp_name', { defaultValue: 'Framework name' })} value={name} onChange={setName} />
                <Field label={t('imp_category', { defaultValue: 'Category' })} value={category} onChange={setCategory} placeholder="e.g. NGO" />
                <Field label={t('imp_industry', { defaultValue: 'Industry' })} value={industry} onChange={setIndustry} />
              </div>

              {mode === 'ai' ? (
                <div className="space-y-3">
                  {!ai ? (
                    <Button onClick={runAi} disabled={aiBusy} className="w-full">
                      {aiBusy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
                      {aiBusy
                        ? t('imp_ai_working', { defaultValue: 'Dana is reading the worksheet…' })
                        : t('imp_ai_go', { defaultValue: 'Let Dana build the framework' })}
                    </Button>
                  ) : (
                    <div className="rounded-lg border border-border p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-medium text-foreground flex items-center gap-1">
                          <Sparkles className="w-3.5 h-3.5 text-primary-500" />
                          {t('imp_ai_proposal', { defaultValue: "Dana's proposal" })}
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={ai.confidence >= 70 ? 'secondary' : 'outline'}>
                            {t('imp_confidence', { defaultValue: '{{n}}% confident', n: Math.round(ai.confidence) })}
                          </Badge>
                          <button type="button" className="text-xs text-muted-foreground underline" onClick={runAi} disabled={aiBusy}>
                            {t('imp_ai_redo', { defaultValue: 'Redo' })}
                          </button>
                        </div>
                      </div>
                      <SummaryBadges s={countDef(ai.definition)} rows={parsed.rows.length} t={t} />
                      {ai.notes && <p className="text-xs text-muted-foreground">{ai.notes}</p>}
                    </div>
                  )}
                </div>
              ) : (
                map && (
                  <>
                    {/* Column roles */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <ColSelect label={t('imp_grade_col', { defaultValue: 'Grade column' })} value={map.gradeCol}
                        options={headerOptions} allowNone={false} onChange={(v) => setMap({ ...map, gradeCol: v })} />
                      <ColSelect label={t('imp_label_col', { defaultValue: 'Grade label / titles' })} value={map.labelCol}
                        options={headerOptions} allowNone onChange={(v) => setMap({ ...map, labelCol: v })} />
                      <ColSelect label={t('imp_comp_col', { defaultValue: 'Competencies column' })} value={map.competencyCol}
                        options={headerOptions} allowNone onChange={(v) => setMap({ ...map, competencyCol: v })} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">
                        {t('imp_factors', { defaultValue: 'Descriptor columns → factors (weights split evenly)' })}
                      </label>
                      <div className="flex flex-wrap gap-1.5">
                        {headerOptions
                          .filter((h) => ![map.gradeCol, map.labelCol, map.competencyCol].includes(h))
                          .map((h) => {
                            const on = map.factorCols.includes(h);
                            return (
                              <button type="button" key={h} onClick={() => toggleFactor(h)}
                                className={`px-2.5 py-1 rounded-md text-xs border ${
                                  on ? 'bg-primary-500/10 border-primary-500 text-primary-500' : 'bg-card border-border text-muted-foreground'
                                }`}>
                                {h}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                    {manualBuilt && (
                      <div className="rounded-lg border border-border p-3 space-y-2">
                        <div className="text-xs font-medium text-foreground">{t('imp_preview', { defaultValue: 'Preview' })}</div>
                        <SummaryBadges s={manualBuilt.summary} rows={parsed.rows.length} t={t} />
                        {manualBuilt.warnings.length > 0 && (
                          <ul className="text-xs text-warning list-disc pl-4">
                            {manualBuilt.warnings.map((w, i) => <li key={i}>{w}</li>)}
                          </ul>
                        )}
                      </div>
                    )}
                  </>
                )
              )}

              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <FileSpreadsheet className="w-3 h-3" />
                {t('imp_note', {
                  defaultValue: 'The engine re-validates the framework on create; evaluation scoring stays a transparent rule engine (no AI at run time).',
                })}
              </p>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={create} disabled={saving || !canCreate}>
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {t('imp_create', { defaultValue: 'Create framework' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ModeBtn: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({
  active, onClick, icon, label,
}) => (
  <button type="button" onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium ${
      active ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
    }`}>
    {icon}{label}
  </button>
);

const SummaryBadges: React.FC<{ s: { grades: number; factors: number; competencies: number }; rows: number; t: any }> = ({
  s, rows, t,
}) => (
  <div className="flex flex-wrap gap-2">
    <Badge variant="secondary">{s.grades} {t('imp_grades', { defaultValue: 'grades' })}</Badge>
    <Badge variant="secondary">{s.factors} {t('imp_factors_n', { defaultValue: 'factors' })}</Badge>
    <Badge variant="secondary">{s.competencies} {t('imp_comps', { defaultValue: 'competencies' })}</Badge>
    <Badge variant="outline">{rows} {t('imp_rows', { defaultValue: 'rows' })}</Badge>
  </div>
);

const Field: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string }> = ({
  label, value, onChange, placeholder,
}) => (
  <div className="space-y-1">
    <label className="text-xs text-muted-foreground">{label}</label>
    <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

const ColSelect: React.FC<{
  label: string; value: string; options: string[]; allowNone: boolean; onChange: (v: string) => void;
}> = ({ label, value, options, allowNone, onChange }) => (
  <div className="space-y-1">
    <label className="text-xs text-muted-foreground">{label}</label>
    <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
      {allowNone && <option value="">—</option>}
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default ImportFrameworkDialog;
